import { useContext, useMemo, useRef } from "react";
import { AppContext } from "contexts/AppContext";
import useAuth from "../useAuth";
import useWebotServer from "../useWebotServer";
import {
  isContactDuplicateError,
  isContactNotInYourBookError,
  toContactWriteError
} from "utils/contacts/contactWriteErrors";
import { ownContactIdsOfSelection } from "utils/contacts/legacyBook";
import { contactLookupStore } from "./contactLookupStore";
import { contactDocumentStore } from "./contactDocumentStore";
import { contactIdsWrittenByImport, reviveContactDocument } from "./contactDocumentRules";
import { pickSummaryFields, rowToContactSummary, summaryOfCreatedContact } from "./contactSummary";
import { isOrgBookKey } from "./contactBookRules";
import useContactBook from "./useContactBook";
import { MAX_BULK_CREATE_CONTACTS } from "../../@types/webotServer";
import type { RealEstateContact } from "../../@types/sharedSchema";
import type {
  CrmContactCreateRequest,
  CrmContactCreateResponse,
  CrmContactUpdateResponse,
  CrmContactsBulkUpdateRequest,
  CrmContactsBulkUpdateResponse,
  CrmContactsDeleteRequest,
  CrmContactsDeleteResponse,
  CrmRecordBulkMessageRequest,
  CrmRecordBulkMessageResponse
} from "../../@types/webotServer";
import type { REST_schema } from "../../@types/sharedSchema";

/**
 * THE ONE PLACE THE DASHBOARD WRITES A CONTACT THROUGH THE SERVER.
 *
 * ── Why a chokepoint, and not just the API client ─────────────────────────────────
 *
 * A contact write has two halves: the request, and telling every local copy of the book
 * that it happened. For years the second half was free — the two browser writers
 * (`updateContact`, `batchUpdate`) patched the in-memory array as a side effect, so no call
 * site ever had to think about it.
 *
 * Then creates moved to `POST /contacts/create` and imports to `/bulk-create`, called
 * straight from the dialog and the import screen. The request half was correct and the
 * other half simply stopped happening: the old contacts page, which renders that array,
 * showed no new contact until the browser was reloaded (Trello #207). N call sites, each
 * having to remember M local stores, is a bug that is written again every time either
 * number grows.
 *
 * So a write goes through here, and here is the only code that knows which stores exist.
 * Adding a store is one edit in this file; adding a call site cannot forget one.
 *
 * (The writers that still live in `AppContext` — the browser-direct `updateContact` /
 * `batchUpdate` / `batchDelete` of the two legacy tables, and `updateExistingContact`, which
 * `updateContact` below delegates to — patch the same two stores next to their own write.)
 *
 * ── Why the browser does not write a contact document itself any more ──────────────
 *
 * `AppContext.updateContact` is `set(merge: true)` from the browser, and it decides whether
 * it is editing or creating by looking the contact up in `allContacts`. On a screen that
 * has not downloaded the whole book — which is every screen but two — that lookup always
 * says "new": the write is normalised as a create, and the in-memory array becomes
 * `[thatOneContact]`. It also bypasses the field whitelist, the audit row, the blocked-list
 * reconciliation and the inline mirror sync that every server write gets. `saveContact`
 * below is what that upsert becomes when the server decides which half it is.
 *
 * ── The three local stores, and what each write does to them ───────────────────────
 *
 * THE LEGACY BOOK (`useLegacyContactsBook`) — the whole book, held only while one of the two
 * legacy tables has armed it; every call below is a no-op otherwise.
 *
 * THE LOOKUP STORE (`contactLookupStore`) — the names behind the phones on screen, read by
 * `useContactsLookup`. It holds only what was asked about, so a write-through to a phone
 * nobody asked about costs a map entry.
 *
 * THE DOCUMENT STORE (`contactDocumentStore`) — the whole document of every contact a card,
 * panel or dialog has open, read by `useContactDocument` / `useContactByPhone` /
 * `useLiveContact`. The create and update responses carry the document as PERSISTED
 * (`doc` + `version`), so it is written straight in: the open panel and the row beside it
 * repaint in the response tick, before the contact's signal document lands.
 *
 *                   legacy book                  lookup store                 document store
 *  - create        put in now                   the server's row put in      the response's doc put in
 *  - update        patched (optimistically,     the server's row put in      the response's doc put in
 *                  rolled back on failure)
 *  - record send   patched if present           patched if held              that id invalidated
 *  - import        INVALIDATED once, at the end invalidated: readers re-ask  the ids the server WROTE invalidated
 *  - delete        named ids removed; a filter  named ids marked absent;     named ids marked absent;
 *                  selection invalidates        a filter selection invalidates  a filter selection invalidates
 *  - bulk update   invalidated                  invalidated                  invalidated
 *  - re-key        row moves to the new id      old forgotten, new re-asked  old absent, new re-asked
 *  - holder add    invalidated                  invalidated                  that id invalidated
 *  - holder remove self: named id removed       invalidated                  that id invalidated
 *
 * ── The BOOK the stores are keyed by is the server's answer, not the email ──────────
 *
 * Every "is this my book" test here compares against `useContactBook().ownBookKey` — the
 * signed-in email for a personal book, `org:<orgId>` for a member of a pooled agency (the
 * server's plan D4). For the whole fleet today the two are the same string, so nothing
 * changes; for a pooled member the response's `ownerUserId` is the office's key, and
 * comparing it against the email would have skipped every own-book store update.
 *
 * In a pooled book "remove from my list" (a holder removal, and the default meaning of a
 * delete there) does NOT make the record absent: the office still holds it, and a reader
 * on the office tab still shows it. Those paths INVALIDATE the entry instead of marking it
 * absent, so each reader re-asks under its own scope — a member's own-list card learns
 * "not on my list", the office tab's panel keeps the record.
 *
 * Why an import invalidates rather than patches: it is thousands of rows whose stored form
 * the browser cannot predict (the server normalizes ids and derives fields), so patching
 * would build approximate documents. The document store is told WHICH ids, though — the
 * server's results name them as filed (`contactIdsWrittenByImport`): invalidating every
 * held document made every open card refetch for an import that touched none of them. Why
 * a filter selection invalidates everything: the SERVER resolved the ids, and they never
 * came here.
 *
 * The functions are referentially STABLE (`useWebotServer()` rebuilds its whole value on
 * every render), so they are safe in dependency arrays.
 */

export type ContactImportRow = { contactId: string; fields: Record<string, unknown> };

export type ContactImportFailure = { requestedContactId: string; error?: string };

export type ContactImportResult = {
  /** Rows the server created or updated. */
  written: number;
  /** Rows the server refused INDIVIDUALLY (an unparseable phone, a rejected field). */
  failures: ContactImportFailure[];
};

export type ContactImportOptions = {
  /** Stamped as `contactOrigin` on rows that carry none. */
  defaultContactOrigin?: string;
  /** Called after every chunk, so a 30,000-row import shows a number instead of a spinner. */
  onProgress?: (written: number, total: number) => void;
};

/**
 * A COLLEAGUE's book, for the agency view — the one case where the contact being written
 * is not the signed-in user's own. The server intersects the named owner with the caller's
 * resolved scope, so naming one can never reach outside it.
 */
export type ContactOwnerScope = { ownerUserId: string; orgId?: string };

export type SaveContactRequest = {
  /** The contact's phone. An existing contact is addressed by its exact id. */
  contactId: string;
  /** Written either way. Must carry at least one field. */
  fields: Record<string, unknown>;
  /** Added only when the contact has to be CREATED — its origin, and whatever else a new card needs. */
  fieldsOnCreate?: Record<string, unknown>;
};

export type SaveContactResult = {
  /** The id the contact is filed under — the server normalizes a new contact's phone. */
  contactId: string;
  created: boolean;
};

export type ContactWrites = {
  /** One new contact. Rejects with the server's 409 when the number is taken. */
  createContact: (request: CrmContactCreateRequest) => Promise<CrmContactCreateResponse>;
  /**
   * Edit a contact that EXISTS. Never creates: rejects with `ContactNotInYourBookError`
   * when there is no such contact (deleted elsewhere, or never was one). An empty `fields`
   * is a no-op, not a request.
   *
   * The caller's own book unless `owner` names a colleague's. An own-book edit shows at
   * once (the local stores are patched before the request and rolled back if it fails); a
   * colleague's has no local copy in this browser to patch.
   */
  updateContact: (
    contactId: string,
    fields: Record<string, unknown>,
    owner?: ContactOwnerScope
  ) => Promise<CrmContactUpdateResponse | void>;
  /**
   * "Make sure this contact has these fields" — edit it if it exists, create it if not.
   *
   * The server decides which, so this is correct on a screen that has never loaded the book.
   * A contact that appears between the two calls (a call being transcribed, another tab) is
   * edited rather than failed.
   */
  saveContact: (request: SaveContactRequest) => Promise<SaveContactResult>;
  /**
   * Change a contact's PHONE — a server-side re-key (the document id IS the phone): the
   * contact and everything attached to it move to the new number and the old document is
   * deleted. Both local stores follow: the legacy book's row moves to the new id, and the
   * lookup store forgets the old number and re-asks about the new one.
   */
  changeContactPhone: (
    request: REST_schema.ChangeContactPhoneRequest
  ) => Promise<REST_schema.ChangeContactPhoneResponse>;
  /**
   * Record what one bulk WhatsApp send did. For a phone that is not a contact — normal for
   * a send to a number from a file — it RESOLVES with `notAContact: true`, having written
   * nothing. The endpoint never creates a contact.
   */
  recordBulkMessage: (request: CrmRecordBulkMessageRequest) => Promise<CrmRecordBulkMessageResponse>;
  /**
   * The import's upsert, chunked to the endpoint's cap and sent SEQUENTIALLY — each row fans
   * out to six Cloud Function triggers, and firing every chunk at once turns the burst the
   * mirror's queue is sized for into a much larger one.
   *
   * A request-level failure rejects; rows the server refused individually come back in
   * `failures` and are NOT an error — a real sheet always carries a few unparseable phones.
   */
  importContacts: (rows: ContactImportRow[], options?: ContactImportOptions) => Promise<ContactImportResult>;
  /**
   * Deletes, with the pooled book's two meanings: on a personal book the documents are
   * gone; on a pooled book the caller is taken OFF the records' holder lists and the
   * office's records stand, unless `deleteOfficeRecord: true` — which the server allows
   * only with the `delete` permission and the full pool. The response's `mode` says which
   * happened, and the stores are told accordingly.
   */
  deleteContacts: (request: CrmContactsDeleteRequest) => Promise<CrmContactsDeleteResponse>;
  bulkUpdateContacts: (request: CrmContactsBulkUpdateRequest) => Promise<CrmContactsBulkUpdateResponse>;
  /**
   * "My contacts" inside a POOLED agency: put a member on a person's list (self by default;
   * somebody else needs an admin). Idempotent. Rejects on a personal book (400), when the
   * office has no record (404) and while the switch runs (503 `CRM_BOOK_BUSY`).
   */
  addContactHolder: (
    request: REST_schema.CrmContactHolderChangeRequest
  ) => Promise<REST_schema.CrmContactHolderAddResponse>;
  /** The reverse. The office's record stands even when the last holder goes. */
  removeContactHolder: (
    request: REST_schema.CrmContactHolderChangeRequest
  ) => Promise<REST_schema.CrmContactHolderRemoveResponse>;
};

const sameUser = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

export default function useContactWrites(): ContactWrites {
  const {
    contactsCreate,
    contactsBulkCreate,
    contactsDelete,
    contactsBulkUpdate,
    contactsRecordBulkMessage,
    contactsChangePhone,
    contactsUpdate,
    contactsHoldersAdd,
    contactsHoldersRemove
  } = useWebotServer();
  const {
    updateExistingContact,
    upsertContactsInMemory,
    patchContactInMemory,
    removeContactsInMemory,
    rekeyContactInMemory,
    invalidateContactsBook
  } = useContext(AppContext);
  const { user } = useAuth();
  // The book the stores are keyed by — see the header. `ownBookKey` is the email until the
  // server has answered, which is the right key for every personal book and a placeholder
  // for a pooled member's first frame.
  const { ownBookKey } = useContactBook();

  // Everything the writes need, read through a ref so the returned functions never change
  // identity while always calling the latest closures.
  const current = {
    contactsCreate,
    contactsBulkCreate,
    contactsDelete,
    contactsBulkUpdate,
    contactsRecordBulkMessage,
    contactsChangePhone,
    contactsUpdate,
    contactsHoldersAdd,
    contactsHoldersRemove,
    updateExistingContact,
    upsertContactsInMemory,
    patchContactInMemory,
    removeContactsInMemory,
    rekeyContactInMemory,
    invalidateContactsBook,
    myUserId: (user?.email as string | undefined) ?? null,
    myBookKey: ownBookKey
  };
  const latest = useRef(current);
  latest.current = current;

  return useMemo<ContactWrites>(() => {
    /**
     * The update response's document into the document store, for whichever book the
     * server wrote. `doc` is null only when the read-back after the write failed (logged
     * and metered server-side); the signal, or the next ask, then brings the document.
     */
    const writeDocumentThrough = (response: CrmContactUpdateResponse): void => {
      if (!response.doc) return;
      contactDocumentStore.put(
        response.ownerUserId,
        response.contactId,
        reviveContactDocument(response.doc),
        response.version
      );
    };

    /**
     * A write CLAIMS the document's entry before its request leaves, and withdraws the
     * claim once the response is in (`put` settles it) or the request failed.
     *
     * The server writes the contact's signal INSIDE the write request — the inline mirror
     * sync drains the outbox before the response's document is read back — so the saving
     * tab's own listener usually delivers `v = N` while the response is still in flight.
     * Without a claim the signal reads "newer than what I hold" and costs a
     * `POST /contacts/get` that the response then overrides: one redundant fetch per own
     * save, counted by the shadow meter as a signal-triggered fetch — the very double the
     * plan's P1 verification wants at zero. In flight, the verdict waits; `put` settles the
     * entry with the response's document and version, and the signal then says nothing new.
     *
     * Returned is the withdrawal: a no-op when the entry was already in flight (a fetch
     * owns it, and releasing a claim one does not own would discard that fetch's answer),
     * and a release WITHOUT an error otherwise — a failed write is not a failed read, and a
     * request that did not land may still have landed server-side, which the signal tells.
     */
    const claimForWrite = (bookKey: string | null, contactId: string): (() => void) => {
      if (!bookKey || !contactDocumentStore.claim(bookKey, contactId)) return () => undefined;
      const session = contactDocumentStore.session();
      return () => contactDocumentStore.release(session, bookKey, [contactId]);
    };

    /** The server files a create under the NORMALIZED phone; only an id already in that shape is the id it will answer with. */
    const isDocumentIdShaped = (contactId: string): boolean => /^\+\d{7,15}$/.test(contactId);

    const createContact: ContactWrites["createContact"] = async (request) => {
      // Claimed only when the request's id IS the document id: a claim on a spelling the
      // server normalizes away would never be settled by the response's `put`, and a reader
      // of that spelling would wait on it forever.
      const withdraw = isDocumentIdShaped(request.contactId)
        ? claimForWrite(request.ownerUserId ?? latest.current.myBookKey, request.contactId)
        : () => undefined;
      let created: Awaited<ReturnType<typeof latest.current.contactsCreate>>;
      try {
        created = await latest.current.contactsCreate(request);
      } finally {
        // Before the write-through, so a response whose id differs from the claim leaves no
        // claim behind; after it `put` has already settled the entry and this is a no-op.
        withdraw();
      }

      // The document store takes the document for ANY book (it is keyed by book): a card
      // already open on this phone — a seller badge, the calls table — repaints now.
      if (created.doc) {
        contactDocumentStore.put(
          created.ownerUserId,
          created.contactId,
          reviveContactDocument(created.doc),
          created.version
        );
      }

      // Only the caller's own book is held in memory; a contact created in a colleague's
      // book (the endpoint accepts an owner) belongs to an array this browser does not have.
      // A pooled `alreadyInOffice` answer takes the same path: the person is on the caller's
      // list now, which is exactly what their own book is.
      if (sameUser(created.ownerUserId, latest.current.myBookKey)) {
        contactLookupStore.put(
          created.contact
            ? rowToContactSummary(created.contact)
            : summaryOfCreatedContact(created.contactId, created.ownerUserId, request.fields)
        );

        const contactOrigin = (request.fields.contactOrigin as string | undefined) ?? request.defaultContactOrigin;
        latest.current.upsertContactsInMemory([
          {
            ...(request.fields as Partial<RealEstateContact>),
            ...(contactOrigin ? { contactOrigin } : {}),
            // The server NORMALIZES the phone; the id it filed the contact under is the id.
            id: created.contactId,
            callerPhoneNumber: created.contactId
          }
        ]);
      }

      return created;
    };

    const updateContact: ContactWrites["updateContact"] = async (contactId, fields, owner) => {
      // An owner naming the caller's OWN book — the email, or the office's `org:` key for a
      // pooled member — is the own-book path: a pooled member must never address their own
      // office record as `view: "org"`, which the server refuses to a member without `browse`.
      if (owner && !sameUser(owner.ownerUserId, latest.current.myBookKey)) {
        const withdraw = claimForWrite(owner.ownerUserId, contactId);
        try {
          const response = await latest.current.contactsUpdate({
            contactId,
            ownerUserId: owner.ownerUserId,
            view: "org",
            ...(owner.orgId ? { orgId: owner.orgId } : {}),
            fields
          });
          writeDocumentThrough(response);
          return response;
        } catch (error) {
          throw toContactWriteError(error, contactId);
        } finally {
          withdraw();
        }
      }

      // The caller's own book. The request, the optimistic patch of the legacy book with
      // its rollback, the lookup store's write-through and the 404 →
      // `ContactNotInYourBookError` mapping all live in `AppContext.updateExistingContact`,
      // beside the array it patches. The document store is this hook's: the entry is claimed
      // before the request leaves (see `claimForWrite`), and the response's `doc` — the
      // document as persisted, redacted with the request's own flags — settles it.
      const withdraw = claimForWrite(latest.current.myBookKey, contactId);
      try {
        const response = await latest.current.updateExistingContact(contactId, fields, null);
        if (response) writeDocumentThrough(response);
        return response;
      } finally {
        withdraw();
      }
    };

    const saveContact: ContactWrites["saveContact"] = async ({ contactId, fields, fieldsOnCreate }) => {
      try {
        await updateContact(contactId, fields);
        return { contactId, created: false };
      } catch (error) {
        if (!isContactNotInYourBookError(error)) throw error;
      }

      try {
        const created = await createContact({ contactId, fields: { ...fields, ...fieldsOnCreate } });
        return { contactId: created.contactId, created: true };
      } catch (error) {
        // It did not exist a moment ago and it does now: something else created it between
        // the two calls. That is the first case again, not a failure.
        if (!isContactDuplicateError(error)) throw error;
        await updateContact(contactId, fields);
        return { contactId, created: false };
      }
    };

    const changeContactPhone: ContactWrites["changeContactPhone"] = async (request) => {
      const response = await latest.current.contactsChangePhone(request);
      // The server succeeded, so there is nothing to undo: the old document is gone. This
      // moves the legacy book's row and repairs the lookup store (see `AppContext`).
      latest.current.rekeyContactInMemory(request.contactId, response.newContactId);
      // The document store is keyed by id too: the old number is nobody now, and whatever
      // it holds about the new one ("not a contact", most likely) stopped being true.
      if (latest.current.myBookKey) {
        contactDocumentStore.markAbsent(latest.current.myBookKey, [request.contactId]);
        contactDocumentStore.invalidate(latest.current.myBookKey, [response.newContactId]);
      }
      return response;
    };

    const recordBulkMessage: ContactWrites["recordBulkMessage"] = async (request) => {
      // "Not a contact" comes back as an ordinary reply (`written: false, notAContact: true`),
      // so whatever rejects here is a real failure and keeps its own identity.
      const response: CrmRecordBulkMessageResponse = await latest.current.contactsRecordBulkMessage(request);

      if (response.written && sameUser(response.ownerUserId, latest.current.myBookKey)) {
        // What the server stamped, as nearly as the browser can know it: the next audience
        // built in this session must already skip someone messaged a minute ago.
        const sentTime = new Date().toISOString();
        const patch: Record<string, unknown> =
          request.outcome === "sent"
            ? {
                lastBulkMessageReceived: {
                  message: request.sentMessage?.message ?? "",
                  mediaLink: request.sentMessage?.mediaLink ?? "",
                  sentTime,
                  sentTo: response.contactId,
                  updatedFrom: latest.current.myUserId
                },
                lastBulkMessageReceivedTime: sentTime
              }
            : { optedOutOfBulkMessages: true, bulkMessagingNumberDoesntExists: true };

        latest.current.patchContactInMemory(response.contactId, patch);
        contactLookupStore.patch(response.contactId, pickSummaryFields(patch));
        // The document store holds whole documents and takes no patch: a card open on this
        // contact re-asks for it, and gets the stamp as the server wrote it.
        contactDocumentStore.invalidate(response.ownerUserId, [response.contactId]);
      }

      return response;
    };

    const importContacts: ContactWrites["importContacts"] = async (rows, options = {}) => {
      let written = 0;
      const failures: ContactImportFailure[] = [];
      // The book the server wrote into and the ids it filed, across every chunk that landed
      // — what the document store is told at the end.
      let book: string | null = null;
      const writtenIds = new Set<string>();

      try {
        for (let start = 0; start < rows.length; start += MAX_BULK_CREATE_CONTACTS) {
          const response = await latest.current.contactsBulkCreate({
            contacts: rows.slice(start, start + MAX_BULK_CREATE_CONTACTS),
            defaultContactOrigin: options.defaultContactOrigin
          });
          written += response.created + response.updated;
          book = response.ownerUserId;
          for (const contactId of contactIdsWrittenByImport(response.results)) writtenIds.add(contactId);
          failures.push(
            ...response.results
              .filter((result) => result.status === "failed")
              .map(({ requestedContactId, error }) => ({ requestedContactId, error }))
          );
          options.onProgress?.(written, rows.length);
        }
      } finally {
        // Also on a failure half-way: the chunks before it landed, and the stores are
        // wrong about exactly those rows.
        if (written > 0) {
          latest.current.invalidateContactsBook();
          contactLookupStore.invalidate();
          // Named ids: the document store holds the whole documents of OPEN cards, and
          // invalidating all of it made every one of them refetch for an import that
          // touched none of them — on top of the imported contacts' own signals.
          if (book && writtenIds.size > 0) contactDocumentStore.invalidate(book, Array.from(writtenIds));
        }
      }

      return { written, failures };
    };

    const deleteContacts: ContactWrites["deleteContacts"] = async (request) => {
      const response = await latest.current.contactsDelete(request);
      // "Remove from my list" — the pooled book's default. The office's record STANDS, so
      // nothing below may claim it is gone: the stores are invalidated rather than marked
      // absent, and each reader re-asks under its own scope (see the header).
      const removedFromList = response.mode === "holder";

      const named = latest.current.myBookKey
        ? ownContactIdsOfSelection(request.selection, latest.current.myBookKey)
        : null;
      if (named === null) {
        if (response.deleted > 0) {
          latest.current.invalidateContactsBook();
          contactLookupStore.invalidate();
          contactDocumentStore.invalidate();
        }
      } else {
        // A contact the server could not delete is still there; one it reports as already
        // gone is exactly as absent as one it just removed. A pooled `not_held` is "was not
        // on my list to begin with" — nothing changed for it either way.
        const stillThere = new Set(
          response.skipped
            .filter((skip) => skip.reason === "error" || skip.reason === "not_held")
            .map((skip) => skip.contactId)
        );
        const gone = named.filter((contactId) => !stillThere.has(contactId));
        // The legacy book is the caller's OWN list in both modes, so the rows leave it either way.
        latest.current.removeContactsInMemory(gone);
        if (removedFromList) {
          if (gone.length > 0) contactLookupStore.invalidate();
          if (latest.current.myBookKey) contactDocumentStore.invalidate(latest.current.myBookKey, gone);
        } else {
          contactLookupStore.markAbsent(gone);
          if (latest.current.myBookKey) contactDocumentStore.markAbsent(latest.current.myBookKey, gone);
        }
      }

      return response;
    };

    /**
     * A holder change is a change to WHOSE LIST a record is on, never to the record. For
     * the caller's own list it changes what their own-scope readers see (their card of the
     * person, their legacy book, their lookup's `held`), so those are invalidated — not
     * marked absent: the office's record is exactly where it was.
     */
    const holderListChanged = (contactId: string, userId: string | undefined): void => {
      const self = !userId || sameUser(userId, latest.current.myUserId);
      if (!self) return;
      contactLookupStore.invalidate();
      const book = latest.current.myBookKey;
      if (book && isOrgBookKey(book)) contactDocumentStore.invalidate(book, [contactId]);
      latest.current.invalidateContactsBook();
    };

    const addContactHolder: ContactWrites["addContactHolder"] = async (request) => {
      const response = await latest.current.contactsHoldersAdd(request);
      if (response.added) holderListChanged(response.contactId, request.userId);
      return response;
    };

    const removeContactHolder: ContactWrites["removeContactHolder"] = async (request) => {
      const response = await latest.current.contactsHoldersRemove(request);
      if (response.removed) {
        holderListChanged(response.contactId, request.userId);
        if (!request.userId || sameUser(request.userId, latest.current.myUserId)) {
          latest.current.removeContactsInMemory([response.contactId]);
        }
      }
      return response;
    };

    const bulkUpdateContacts: ContactWrites["bulkUpdateContacts"] = async (request) => {
      const response = await latest.current.contactsBulkUpdate(request);
      if (response.updated > 0) {
        latest.current.invalidateContactsBook();
        contactLookupStore.invalidate();
        contactDocumentStore.invalidate();
      }
      return response;
    };

    return {
      createContact,
      updateContact,
      saveContact,
      changeContactPhone,
      recordBulkMessage,
      importContacts,
      deleteContacts,
      bulkUpdateContacts,
      addContactHolder,
      removeContactHolder
    };
  }, []);
}
